import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { usePager, Pager } from "@/components/kiyana/Pager";
import { HandCoins, Plus, X, Check, Ban } from "lucide-react";

const inp = "h-9 px-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]";
const KB_LABEL = { diajukan: "Diajukan", disetujui: "Disetujui", lunas: "Lunas", ditolak: "Ditolak" };
const KB_CLS = { diajukan: "bg-amber-50 text-amber-600", disetujui: "bg-blue-50 text-blue-600", lunas: "bg-emerald-50 text-emerald-600", ditolak: "bg-slate-100 text-slate-400" };

export default function AdminKasbon() {
  const [items, setItems] = useState(null);
  const { paged: pagedKb, pager: kbPager } = usePager(items || []);
  const [status, setStatus] = useState("");
  const [emps, setEmps] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ user_id: "", amount: "", installments: 4, reason: "" });
  const [reject, setReject] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(() => {
    setItems(null);
    api.get("/admin/kasbon", { params: { status } }).then((r) => setItems(r.data)).catch((e) => { setItems(false); toast.error(e?.response?.data?.detail || "Gagal memuat kasbon"); });
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/employees").then((r) => setEmps((r.data || []).filter((u) => u.role !== "pelanggan"))).catch(() => {}); }, []);

  const create = async () => {
    if (!form.user_id) return toast.error("Pilih karyawan");
    if (!(parseInt(form.amount) >= 10000)) return toast.error("Nominal kasbon minimal Rp10.000");
    try {
      await api.post("/admin/kasbon", { user_id: form.user_id, amount: parseInt(form.amount), installments: parseInt(form.installments) || 1, reason: form.reason });
      toast.success("Kasbon diajukan — menunggu persetujuan Owner/HR");
      setShowForm(false);
      setForm({ user_id: "", amount: "", installments: 4, reason: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengajukan kasbon"); }
  };

  const approve = async (k) => {
    if (!window.confirm(`Setujui kasbon ${k.user_name} sebesar ${formatIDR(k.amount)} (${k.installments}× angsuran)?`)) return;
    try { await api.put(`/admin/kasbon/${k.id}/approve`); toast.success("Kasbon disetujui — cicilan otomatis masuk payroll"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui"); }
  };

  const doReject = async () => {
    try { await api.put(`/admin/kasbon/${reject.id}/reject`, { reason: rejectReason }); toast.success("Kasbon ditolak"); setReject(null); setRejectReason(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak"); }
  };

  return (
    <div data-testid="kasbon-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Kasbon Karyawan</h1>
          <p className="text-xs text-slate-400">Pengajuan kasbon, persetujuan Owner/HR — cicilan otomatis terpotong di payroll.</p>
        </div>
        <button data-testid="kb-create" onClick={() => setShowForm(true)} className="h-9 px-4 rounded-lg bg-[#7C2D3E] hover:bg-[#66222f] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Buat Kasbon
        </button>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["", "Semua"], ["diajukan", "Diajukan"], ["disetujui", "Disetujui"], ["lunas", "Lunas"], ["ditolak", "Ditolak"]].map(([v, l]) => (
          <button key={v} data-testid={`kb-tab-${v || "all"}`} onClick={() => setStatus(v)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${status === v ? "border-[#C4738A] text-[#C4738A]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 font-semibold">Karyawan</th>
              <th className="text-right px-3 py-2.5 font-semibold">Nominal</th>
              <th className="text-right px-3 py-2.5 font-semibold">Angsuran/Bulan</th>
              <th className="text-left px-3 py-2.5 font-semibold w-52">Progres Cicilan</th>
              <th className="text-left px-3 py-2.5 font-semibold">Alasan</th>
              <th className="text-center px-3 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items === null && <tr><td colSpan={7} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
            {pagedKb.map((k) => {
              const per = Math.floor(k.amount / k.installments);
              const sisa = k.amount - per * Math.min(k.paid_count, k.installments);
              const pct = Math.min(100, Math.round((k.paid_count / k.installments) * 100));
              return (
                <tr key={k.id} data-testid={`kb-row-${k.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                        {k.photo ? <img src={k.photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-slate-400">{(k.user_name || "?")[0]}</div>}
                      </div>
                      <div><div className="text-xs font-semibold text-slate-800">{k.user_name}</div>
                        <div className="text-[9px] text-slate-400">{k.employee_id || ""}{k.created_at ? ` • ${formatDateID(k.created_at)}` : ""}</div></div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs font-bold text-slate-800">{formatIDR(k.amount)}</td>
                  <td className="px-3 py-2.5 text-right text-xs text-slate-500">{formatIDR(per)} <span className="text-[9px] text-slate-400">× {k.installments}</span></td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-[#C4738A] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap">{k.paid_count}/{k.installments}</span>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-0.5">Sisa {formatIDR(sisa)}</div>
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-slate-500 max-w-[180px] truncate">{k.reason || "—"}{k.status === "ditolak" && k.reject_reason ? ` (Ditolak: ${k.reject_reason})` : ""}</td>
                  <td className="px-3 py-2.5 text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${KB_CLS[k.status]}`}>{KB_LABEL[k.status] || k.status}</span></td>
                  <td className="px-4 py-2.5">
                    {k.status === "diajukan" && (
                      <div className="flex gap-1 justify-end">
                        <button data-testid={`kb-approve-${k.id}`} onClick={() => approve(k)} title="Setujui" className="w-8 h-8 rounded-lg text-emerald-500 hover:bg-emerald-50 flex items-center justify-center transition-colors"><Check className="w-3.5 h-3.5" /></button>
                        <button data-testid={`kb-reject-${k.id}`} onClick={() => setReject(k)} title="Tolak" className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Ban className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {items && !items.length && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-xs text-slate-400">
                <HandCoins className="w-8 h-8 mx-auto mb-2 text-slate-200" />Belum ada kasbon pada filter ini.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pager {...kbPager} testid="kb-pager" />

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div data-testid="kb-form-modal" className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-sm text-slate-900">Buat Kasbon</h4>
              <button data-testid="kb-form-close" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Karyawan</label>
              <select data-testid="kb-form-emp" value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))} className={`${inp} w-full`}>
                <option value="">Pilih karyawan...</option>
                {emps.map((u) => <option key={u.id} value={u.id}>{u.name}{u.employee_id ? ` (${u.employee_id})` : ""}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Nominal (Rp)</label>
                <input data-testid="kb-form-amount" type="number" min="10000" step="10000" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="600000" className={`${inp} w-full`} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Jumlah Angsuran</label>
                <input data-testid="kb-form-installments" type="number" min="1" max="24" value={form.installments} onChange={(e) => setForm((f) => ({ ...f, installments: e.target.value }))} className={`${inp} w-full`} />
              </div>
            </div>
            {parseInt(form.amount) >= 10000 && parseInt(form.installments) >= 1 && (
              <p className="text-[10px] text-slate-400 bg-slate-50 rounded-lg px-3 py-2">Angsuran per bulan: <b className="text-[#C4738A]">{formatIDR(Math.floor(parseInt(form.amount) / parseInt(form.installments)))}</b> — dipotong otomatis di payroll setelah disetujui.</p>
            )}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Alasan</label>
              <textarea data-testid="kb-form-reason" rows={2} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Keperluan kasbon..."
                className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#C4738A]" />
            </div>
            <div className="flex gap-2 pt-1">
              <button data-testid="kb-form-cancel" onClick={() => setShowForm(false)} className="flex-1 h-9 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold hover:border-slate-300">Batal</button>
              <button data-testid="kb-form-save" onClick={create} className="flex-1 h-9 rounded-lg bg-[#7C2D3E] text-white text-xs font-semibold hover:bg-[#66222f]">Ajukan Kasbon</button>
            </div>
          </div>
        </div>
      )}

      {reject && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setReject(null)}>
          <div data-testid="kb-reject-modal" className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-sm text-slate-900">Tolak Kasbon</h4>
              <button data-testid="kb-reject-close" onClick={() => setReject(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] text-slate-500">Kasbon <b>{reject.user_name}</b> sebesar <b>{formatIDR(reject.amount)}</b> akan ditolak.</p>
            <textarea data-testid="kb-reject-reason" rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Alasan penolakan..."
              className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#C4738A]" />
            <div className="flex gap-2 pt-1">
              <button data-testid="kb-reject-cancel" onClick={() => setReject(null)} className="flex-1 h-9 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold hover:border-slate-300">Batal</button>
              <button data-testid="kb-reject-confirm" onClick={doReject} className="flex-1 h-9 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600">Tolak Kasbon</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
